import React from "react";
import { Box } from "@mui/material";
import {
  BASE_COST_TIER_LABELS,
  BASE_COST_TIER_MAX,
  formatTokenCount,
} from "../../utils/agentBaseCost";

/**
 * Renders an agent's configuration-derived base cost as a relative $ scale
 * paired with the raw token footprint, e.g. "Base cost  $$ · ~1.7k tokens".
 *
 * The $ scale reflects the model-weighted tier; the token count is the
 * model-independent configured footprint. Pass `showLabel={false}` for tight
 * surfaces (cards, node headers, dropdown rows) where the "Base cost" prefix is
 * noise. Colors come from the MUI theme palette so the badge is legible on both
 * dark dialogs and light cards.
 */
export default function AgentBaseCostBadge({
  tier,
  rawTokens,
  showLabel = true,
  sx,
}) {
  const activeTier = Math.min(
    Math.max(Number.isFinite(tier) ? tier : 1, 1),
    BASE_COST_TIER_MAX
  );
  const tokenLabel = `${formatTokenCount(rawTokens)} tokens`;
  const title = `Base cost: ${BASE_COST_TIER_LABELS[activeTier]} (${"$".repeat(
    activeTier
  )}) · ~${tokenLabel} of configured context carried into every turn`;

  return (
    <Box
      component="span"
      title={title}
      aria-label={title}
      sx={{
        display: "inline-flex",
        alignItems: "center",
        flexWrap: "wrap",
        gap: 0.75,
        fontSize: "11px",
        lineHeight: 1,
        color: "text.secondary",
        ...sx,
      }}
    >
      {showLabel ? (
        <Box component="span" sx={{ fontWeight: 600, color: "text.secondary" }}>
          Base cost
        </Box>
      ) : null}
      <Box
        component="span"
        aria-hidden="true"
        sx={{
          display: "inline-flex",
          alignItems: "center",
          gap: "1px",
          fontWeight: 600,
          letterSpacing: "0.02em",
        }}
      >
        {Array.from({ length: BASE_COST_TIER_MAX }, (_, index) => (
          <Box
            component="span"
            key={index}
            sx={{ color: index < activeTier ? "text.primary" : "text.disabled" }}
          >
            $
          </Box>
        ))}
      </Box>
      <Box component="span" aria-hidden="true" sx={{ color: "text.disabled" }}>
        ·
      </Box>
      <Box component="span" sx={{ color: "text.secondary" }}>
        ~{tokenLabel}
      </Box>
    </Box>
  );
}
